import { useContext, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormattedReservationResponse } from '../../useReservation'
import { ActivityState } from '@nxcr-org/web-api-interface/lib/domain_pb'
import { find } from 'lodash'
import { NexusService } from 'global-apis/nexus-service'
import { PricingOption } from '@nxcr-org/web-api-interface/lib/subscription_pb'
import { ClientContext } from '../../../../../../utils/clientContext'

export function useProgramPricingBlock(
  reservation: FormattedReservationResponse
) {
  const CUSTOM_PRICING_OPTION_ID = 'custom-input-pricing-option'
  const vehicleOptions = reservation?.vehicle
  const { clientId } = useContext(ClientContext)
  const [isEdit, setIsEdit] = useState(false)
  const [isCustomPriceInput, setIsCustomPriceInputState] = useState(false)
  const [monthlyPayment, setMonthly] = useState(vehicleOptions.monthly)
  const [startFee, setStartFee] = useState(vehicleOptions.startFee)
  const [selectedOption, setSelectedOption] =
    useState<PricingOption.AsObject>(null)

  useEffect(() => {
    setIsCustomPriceInputState(false)
    setMonthly(vehicleOptions.monthly)
    setStartFee(vehicleOptions.startFee)
  }, [vehicleOptions.monthly, vehicleOptions.startFee])

  const setIsCustomPriceInput = (isEnabled: boolean) => {
    setIsCustomPriceInputState(isEnabled)
    if (isEnabled) {
      setSelectedOption({
        id: CUSTOM_PRICING_OPTION_ID,
      } as PricingOption.AsObject)
      return
    }
    setSelectedOption(null)
  }

  const { data: programOptions } = useQuery({
    queryKey: ['/programOptions', clientId],
    queryFn: () => {
      return NexusService.listProgramPricing(clientId)
    },
      placeholderData: {
        programsList: [],
      },
      enabled: !!clientId,
    }
  )

  const handleChange = (e) => {
    if (e.target.value === CUSTOM_PRICING_OPTION_ID) {
      setIsCustomPriceInput(true)
      return
    }

    const selectedOption = find(
      getPricingOptionsForProgram(
        programOptions?.programsList,
        reservation?.programId
      ),
      {
        id: e.target.value,
      }
    )

    if (!selectedOption) {
      return
    }

    setIsCustomPriceInputState(false)
    setSelectedOption(selectedOption)
    setMonthly(selectedOption.monthlyRent || selectedOption.monthly)
    setStartFee(selectedOption.startFee)
  }

  const cancelChanges = () => {
    setIsEdit(false)
    setIsCustomPriceInputState(false)
    setSelectedOption(null)
    setMonthly(vehicleOptions.monthly)
    setStartFee(vehicleOptions.startFee)
  }

  const isValidSubscriptionStatus = (reservation) => {
    return (
      reservation.customer.activityState ===
        ActivityState.SUBSCRIPTION_ACTIVATED ||
      reservation.customer.activityState ===
        ActivityState.SUBSCRIPTION_CANCELLED
    )
  }

  const savePricing = useMutation({
    mutationFn: () => {
      const hasValidCustomPricing = monthlyPayment > 0 && startFee >= 0
      if (!selectedOption?.id && !isCustomPriceInput) {
        return Promise.reject('Select a new pricing option or cancel')
      }
      if (isCustomPriceInput && !hasValidCustomPricing) {
        return Promise.reject('Enter a valid custom monthly payment and start fee')
      }

      return NexusService.UpdateProgramReservation({
        reservationId: reservation?.reservation.id,
        deposit: vehicleOptions?.deposit,
        color: reservation?.reservation?.color, // using model options object , in previous implimetation only model3 properties were used.
        secondaryColor: reservation?.reservation?.secondaryColor,
        startFee,
        monthlyRent: monthlyPayment,
        make: reservation?.reservation.make,
        model: reservation?.reservation.model,
        postal: reservation?.reservation?.postal,
        trackingId: reservation?.reservation?.id,
        programId: reservation?.programId,
      })
    },
  })
  return {
    isEdit,
    setIsEdit,
    isCustomPriceInput,
    setIsCustomPriceInput,
    customPricingOptionId: CUSTOM_PRICING_OPTION_ID,
    monthlyPayment,
    setMonthly,
    startFee,
    setStartFee,
    handleChange,
    cancelChanges,
    savePricing,
    isValidSubscriptionStatus,
    programPricingOptions: getPricingOptionsForProgram(
      programOptions?.programsList,
      reservation?.programId
    ),
  }
}

export function getPricingOptionsForProgram(programOptions, programId: string) {
  if (!programOptions?.length || !programId) {
    return []
  }

  const programOption = programOptions?.find(
    (option) => option?.id === programId
  )
  const pricingOptions =
    programOption?.pricingOptionsList[0]?.pricingOptionsList

  return pricingOptions
}
